// src/pages/MapSearch.js
// Carte intelligente : clustering, recherche de zone, clic fiable, sans débordement - NE PAS MODIFIER

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from '../utils/leafletConfig';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import LocationPermissionModal from '../components/shared/LocationPermissionModal';
import { formatPriceForMap } from '../utils/formatPrice';
import { textIncludes } from '../utils/normalizeText';
import { APP_CONFIG } from '../constants/config';

const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
};

const FitToListings = ({ listings, userPosition }) => {
  const map = useMap();
  useEffect(() => {
    const points = listings.filter((l) => l.lat && l.lng).map((l) => [parseFloat(l.lat), parseFloat(l.lng)]);
    if (userPosition) points.push([userPosition.lat, userPosition.lng]);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.flyTo(points[0], 14, { duration: 0.8 });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 14, duration: 0.8 });
  }, [listings, userPosition, map]);
  return null;
};

// Détecte les déplacements FAITS PAR L'UTILISATEUR (pas nos propres flyTo programmatiques)
// pour proposer "Rechercher dans cette zone" au bon moment - NE PAS MODIFIER
const UserMoveDetector = ({ onUserMoved }) => {
  useMapEvents({
    dragend: () => onUserMoved(),
    zoomend: (e) => {
      if (e.hard !== true) onUserMoved(); // ignore les zooms programmatiques marqués
    },
  });
  return null;
};

// Regroupe les marqueurs proches à l'écran en clusters, recalculé au zoom/déplacement - NE PAS MODIFIER
const ClusteredMarkers = ({ listings, theme, selectedId, onSelectListing, onSelectCluster }) => {
  const map = useMap();
  const [clusters, setClusters] = useState([]);

  const computeClusters = useCallback(() => {
    const zoom = map.getZoom();
    // À partir d'un certain niveau de zoom, on montre chaque annonce individuellement
    if (zoom >= 15) {
      setClusters(listings.map((l) => ({ type: 'single', listing: l })));
      return;
    }

    const cellSize = 70; // pixels - taille de la grille de regroupement
    const cells = new Map();

    listings.forEach((listing) => {
      if (!listing.lat || !listing.lng) return;
      const point = map.latLngToContainerPoint([parseFloat(listing.lat), parseFloat(listing.lng)]);
      const key = `${Math.floor(point.x / cellSize)}_${Math.floor(point.y / cellSize)}`;
      if (!cells.has(key)) cells.set(key, []);
      cells.get(key).push(listing);
    });

    const result = [];
    cells.forEach((group) => {
      if (group.length === 1) {
        result.push({ type: 'single', listing: group[0] });
      } else {
        const avgLat = group.reduce((sum, l) => sum + parseFloat(l.lat), 0) / group.length;
        const avgLng = group.reduce((sum, l) => sum + parseFloat(l.lng), 0) / group.length;
        result.push({ type: 'cluster', lat: avgLat, lng: avgLng, count: group.length, listings: group });
      }
    });
    setClusters(result);
  }, [listings, map]);

  useEffect(() => {
    computeClusters();
  }, [computeClusters]);

  useMapEvents({
    zoomend: computeClusters,
    moveend: computeClusters,
  });

  const createPriceIcon = (listing) => {
    const priceLabel = formatPriceForMap(listing.price, listing.price_type || listing.priceType);
    const isSelected = selectedId === listing.id;
    return L.divIcon({
      className: 'price-marker-wrap',
      html: `<div class="fynho-marker-pop" style="
        background-color: ${isSelected ? theme.gold : theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A'};
        color: ${isSelected ? '#FFFFFF' : theme.gold};
        border: 2px solid ${theme.gold};
        border-radius: 20px;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        box-shadow: 0 3px 10px rgba(0,0,0,0.25);
        font-family: -apple-system, sans-serif;
      ">${listing.quartier} · ${priceLabel}</div>`,
      iconSize: [120, 32],
      iconAnchor: [60, 16],
    });
  };

  const createClusterIcon = (count) => {
    return L.divIcon({
      className: 'cluster-marker-wrap',
      html: `<div class="fynho-marker-pop" style="
        width: 42px; height: 42px; border-radius: 50%;
        background-color: ${theme.gold};
        color: #FFFFFF;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; font-weight: 700;
        border: 3px solid #FFFFFF;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        font-family: -apple-system, sans-serif;
      ">${count}</div>`,
      iconSize: [42, 42],
      iconAnchor: [21, 21],
    });
  };

  return (
    <>
      {clusters.map((item, index) => {
        if (item.type === 'cluster') {
          return (
            <Marker
              key={`cluster-${index}`}
              position={[item.lat, item.lng]}
              icon={createClusterIcon(item.count)}
              eventHandlers={{ click: () => onSelectCluster(item.lat, item.lng) }}
            />
          );
        }
        const listing = item.listing;
        return (
          <Marker
            key={listing.id}
            position={[parseFloat(listing.lat), parseFloat(listing.lng)]}
            icon={createPriceIcon(listing)}
            eventHandlers={{ click: () => onSelectListing(listing) }}
          >
            <Popup>
              <div style={{ minWidth: '160px' }}>
                <strong>{listing.title}</strong>
                <br />
                {listing.quartier} — {formatPriceForMap(listing.price, listing.price_type || listing.priceType)}
                {listing.distanceKm != null && (<><br />{listing.distanceKm} km</>)}
                <br />
                <button
                  onClick={() => onSelectListing(listing, true)}
                  style={{
                    marginTop: '8px', width: '100%', padding: '7px', backgroundColor: theme.gold,
                    color: '#FFFFFF', border: 'none', borderRadius: '6px', fontSize: '12px',
                    fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Voir l'annonce →
                </button>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

const MapSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [visibleListings, setVisibleListings] = useState([]);
  const [listingsPool, setListingsPool] = useState([]);
  const [defaultListings, setDefaultListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [userPosition, setUserPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([APP_CONFIG.DEFAULT_LAT, APP_CONFIG.DEFAULT_LNG]);
  const [shouldFitBounds, setShouldFitBounds] = useState(false);
  const [searching, setSearching] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [cityQuery, setCityQuery] = useState('');
  const [showSearchAreaBtn, setShowSearchAreaBtn] = useState(false);
  const debounceRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [nearbyResult, poolResult] = await Promise.all([
          apiClient.get(`/endpoints/listings.php?action=nearby&lat=${APP_CONFIG.DEFAULT_LAT}&lng=${APP_CONFIG.DEFAULT_LNG}&radius=30`),
          apiClient.get('/endpoints/listings.php?action=list&status=published&limit=300'),
        ]);
        const nearby = nearbyResult.listings || [];
        const pool = (poolResult.listings || []).filter((l) => l.lat && l.lng);
        setListingsPool(pool);
        const initial = nearby.length > 0 ? nearby : pool;
        setVisibleListings(initial);
        setDefaultListings(initial);
      } catch (err) {
        setVisibleListings([]);
        setDefaultListings([]);
      } finally {
        setTimeout(() => setMapLoading(false), 300);
      }
    };
    loadInitial();
  }, []);

  const categoryChips = [
    { id: 'all', icon: '🔎', label: t('common.all') },
    { id: 'sejours', icon: '🏨', label: t('categories.sejours') },
    { id: 'locations', icon: '🏠', label: t('categories.locations') },
    { id: 'immobilier', icon: '🌍', label: t('categories.immobilier') },
    { id: 'services', icon: '🛎️', label: t('categories.services') },
  ];

  const filterByCategory = useCallback((list, category) => {
    if (!category || category === 'all') return list;
    return list.filter((l) => l.category === category);
  }, []);

  const fetchNearby = useCallback(async (lat, lng, category) => {
    try {
      const params = new URLSearchParams({
        action: 'nearby', lat, lng, radius: 30,
        ...(category && category !== 'all' ? { category } : {}),
      }).toString();
      const result = await apiClient.get(`/endpoints/listings.php?${params}`);
      return result.listings || [];
    } catch (err) {
      return [];
    }
  }, []);

  const handleSearch = useCallback(() => {
    if (!navigator.geolocation) {
      setShowPermissionGuide(true);
      return;
    }
    setSearching(true);
    setStatusMessage(null);
    setCityQuery('');
    setShowSearchAreaBtn(false);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const userPos = { lat: latitude, lng: longitude };
        setUserPosition(userPos);

        const nearby = await fetchNearby(latitude, longitude, activeCategory);

        if (nearby.length > 0) {
          setVisibleListings(nearby);
          setDefaultListings(nearby);
          setStatusMessage(null);
        } else {
          const fallback = filterByCategory(listingsPool, activeCategory);
          setVisibleListings(fallback);
          setDefaultListings(fallback);
          setStatusMessage(
            fallback.length > 0
              ? { text: "Aucune annonce tout près de vous — voici toutes celles disponibles", tone: 'info' }
              : { text: "Aucune annonce disponible pour le moment", tone: 'warning' }
          );
        }
        setSearching(false);
        setShouldFitBounds(true);
      },
      (error) => {
        setSearching(false);
        if (error.code === 1) setShowPermissionGuide(true);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [activeCategory, fetchNearby, filterByCategory, listingsPool]);

  const handleCategorySelect = async (categoryId) => {
    setActiveCategory(categoryId);
    setCityQuery('');
    setStatusMessage(null);
    setShowSearchAreaBtn(false);

    if (userPosition) {
      const nearby = await fetchNearby(userPosition.lat, userPosition.lng, categoryId);
      if (nearby.length > 0) {
        setVisibleListings(nearby);
        setDefaultListings(nearby);
      } else {
        const fallback = filterByCategory(listingsPool, categoryId);
        setVisibleListings(fallback);
        setDefaultListings(fallback);
        setStatusMessage(
          fallback.length > 0
            ? { text: "Aucune annonce à proximité pour cette catégorie — voici tout le stock", tone: 'info' }
            : { text: "Aucune annonce dans cette catégorie", tone: 'warning' }
        );
      }
    } else {
      const filtered = filterByCategory(listingsPool, categoryId);
      setVisibleListings(filtered);
      setDefaultListings(filtered);
      if (filtered.length === 0) {
        setStatusMessage({ text: "Aucune annonce dans cette catégorie", tone: 'warning' });
      }
    }
    setShouldFitBounds(true);
  };

  const handleCityInput = (value) => {
    setCityQuery(value);
    setShowSearchAreaBtn(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (value.trim().length < 2) {
        setVisibleListings(defaultListings);
        setStatusMessage(null);
        return;
      }
      const base = filterByCategory(listingsPool, activeCategory);
      const matches = base.filter((l) => textIncludes(l.city, value) || textIncludes(l.quartier, value));
      setVisibleListings(matches);
      setShouldFitBounds(true);
      setStatusMessage(matches.length === 0 ? { text: `Aucune annonce trouvée pour "${value}"`, tone: 'warning' } : null);
    }, 350);
  };

  const clearCitySearch = () => {
    setCityQuery('');
    setVisibleListings(defaultListings);
    setStatusMessage(null);
    setShouldFitBounds(true);
  };

  // L'utilisateur a déplacé la carte lui-même → propose de chercher dans cette zone précise
  const handleUserMoved = useCallback(() => {
    setShowSearchAreaBtn(true);
  }, []);

  const handleSearchThisArea = () => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = map.getBounds();
    const base = filterByCategory(listingsPool, activeCategory);
    const withinView = base.filter((l) => bounds.contains([parseFloat(l.lat), parseFloat(l.lng)]));
    setVisibleListings(withinView);
    setShouldFitBounds(false); // on garde la vue actuelle, pas de recadrage
    setShowSearchAreaBtn(false);
    setStatusMessage(
      withinView.length === 0
        ? { text: "Aucune annonce dans cette zone", tone: 'warning' }
        : { text: `${withinView.length} annonce${withinView.length > 1 ? 's' : ''} dans cette zone`, tone: 'info' }
    );
  };

  const handleSelectListing = (listing, goDirect = false) => {
    if (goDirect) {
      navigate(`/listing/${listing.slug}`);
      return;
    }
    setSelectedListing(listing);
  };

  const handleSelectCluster = (lat, lng) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo([lat, lng], Math.min(map.getZoom() + 2, 17), { duration: 0.6 });
  };

  const userIcon = useMemo(() => L.divIcon({
    className: 'user-marker',
    html: `<div class="fynho-pulse-dot" style="width: 16px; height: 16px; border-radius: 50%; background-color: #4299E1; border: 3px solid #FFFFFF; box-shadow: 0 0 0 4px rgba(66,153,225,0.3);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  }), []);

  const styles = {
    page: { display: 'flex', flexDirection: 'column', height: '100dvh', maxHeight: '100dvh', backgroundColor: theme.bg, overflow: 'hidden' },
    header: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
      backgroundColor: theme.bg, borderBottom: `1px solid ${theme.borderSoft}`, flexShrink: 0,
    },
    backButton: {
      width: '36px', height: '36px', borderRadius: '50%', backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`, color: theme.textPrimary, fontSize: '17px',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    titleGroup: { flex: 1, minWidth: 0 },
    title: { fontSize: '16px', fontWeight: '700', color: theme.textPrimary },
    countBadge: { fontSize: '11px', color: theme.textSecondary, marginTop: '1px' },
    body: { flex: 1, minHeight: 0, padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
    searchPill: {
      display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
      backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '14px', flexShrink: 0,
    },
    searchIcon: { fontSize: '14px', opacity: 0.6, flexShrink: 0 },
    searchInput: { flex: 1, border: 'none', outline: 'none', backgroundColor: 'transparent', color: theme.textPrimary, fontSize: '13px', fontFamily: 'inherit' },
    clearBtn: { width: '18px', height: '18px', borderRadius: '50%', backgroundColor: theme.bgInput, border: 'none', color: theme.textSecondary, fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    chipsRow: { display: 'flex', gap: '7px', overflowX: 'auto', scrollbarWidth: 'none', flexShrink: 0 },
    chip: { display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 13px', borderRadius: '18px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 200ms' },
    chipActive: { backgroundColor: theme.gold, color: '#FFFFFF', border: `1px solid ${theme.gold}` },
    searchButton: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '11px', flexShrink: 0,
      background: `linear-gradient(135deg, ${theme.gold}, ${theme.mode === 'light' ? '#1F5C3D' : '#3A7D57'})`,
      color: '#FFFFFF', border: 'none', borderRadius: '12px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    },
    statusBanner: (tone) => ({
      padding: '7px 12px', borderRadius: '9px', fontSize: '11px', textAlign: 'center', flexShrink: 0,
      backgroundColor: tone === 'warning' ? 'rgba(237, 137, 54, 0.12)' : 'rgba(66, 153, 225, 0.12)',
      border: `1px solid ${tone === 'warning' ? 'rgba(237, 137, 54, 0.35)' : 'rgba(66, 153, 225, 0.35)'}`,
      color: tone === 'warning' ? '#DD6B20' : '#4299E1',
    }),
    mapCard: {
      position: 'relative', flex: 1, minHeight: 0, borderRadius: '20px', overflow: 'hidden',
      boxShadow: theme.mode === 'light' ? '0 6px 22px rgba(0,0,0,0.10)' : '0 6px 22px rgba(0,0,0,0.45)',
      border: `1px solid ${theme.border}`,
    },
    mapSkeleton: { position: 'absolute', inset: 0, backgroundColor: theme.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 },
    skeletonPulse: { width: '44px', height: '44px', borderRadius: '50%', backgroundColor: theme.goldSoft, animation: 'skeletonPulse 1.2s ease-in-out infinite' },
    searchAreaBtn: {
      position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
      padding: '9px 16px', backgroundColor: theme.mode === 'light' ? '#0F1419' : '#FFFFFF',
      color: theme.mode === 'light' ? '#FFFFFF' : '#0F1419', border: 'none', borderRadius: '20px',
      fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
      display: 'flex', alignItems: 'center', gap: '6px', animation: 'dropIn 250ms ease-out',
    },
    selectedCardWrap: { position: 'absolute', bottom: '12px', left: '12px', right: '12px', zIndex: 1000 },
    selectedCard: {
      display: 'flex', gap: '10px', padding: '11px', backgroundColor: theme.bgCard,
      borderRadius: '14px', border: `1px solid ${theme.goldBorder}`, cursor: 'pointer',
      boxShadow: '0 8px 20px rgba(0,0,0,0.25)', position: 'relative', animation: 'slideUpCard 250ms ease-out',
    },
    cardImage: { width: '64px', height: '64px', borderRadius: '9px', objectFit: 'cover', flexShrink: 0 },
    cardContent: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3px', minWidth: 0 },
    cardTitle: { fontSize: '13px', fontWeight: '600', color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    cardLocation: { fontSize: '11px', color: theme.textSecondary },
    cardPrice: { fontSize: '13px', fontWeight: '700', color: theme.gold },
    closeCardBtn: {
      position: 'absolute', top: '6px', right: '6px', width: '20px', height: '20px', borderRadius: '50%',
      backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.textSecondary,
      fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    navSpacer: { height: '74px', flexShrink: 0 },
  };

  return (
    <div style={styles.page}>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        @keyframes skeletonPulse { 0%, 100% { opacity: 0.5; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes fynhoMarkerPop { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideUpCard { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dropIn { from { opacity: 0; transform: translate(-50%, -8px); } to { opacity: 1; transform: translate(-50%, 0); } }
        .fynho-marker-pop { animation: fynhoMarkerPop 250ms cubic-bezier(0.34, 1.56, 0.64, 1); }
        .fynho-pulse-dot { animation: fynhoMarkerPop 250ms ease-out; }
        .leaflet-container { font-family: -apple-system, sans-serif; }
      `}</style>

      <Helmet>
        <title>Carte des annonces | Fynho</title>
        <meta name="description" content="Explorez les annonces immobilières à proximité sur la carte au Cameroun." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.titleGroup}>
          <div style={styles.title}>🗺️ {t('home.viewMap')}</div>
          <div style={styles.countBadge}>{visibleListings.length} {visibleListings.length > 1 ? t('home.listings') : t('home.listing')}</div>
        </div>
      </div>

      <div style={styles.body}>
        <div style={styles.searchPill}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Rechercher une ville ou un quartier..."
            value={cityQuery}
            onChange={(e) => handleCityInput(e.target.value)}
          />
          {cityQuery && <button style={styles.clearBtn} onClick={clearCitySearch}>✕</button>}
        </div>

        <div style={styles.chipsRow} className="hide-scrollbar">
          {categoryChips.map((chip) => (
            <div
              key={chip.id}
              style={{ ...styles.chip, ...(activeCategory === chip.id ? styles.chipActive : {}) }}
              onClick={() => handleCategorySelect(chip.id)}
            >
              <span>{chip.icon}</span>
              {chip.label}
            </div>
          ))}
        </div>

        <button style={styles.searchButton} onClick={handleSearch} disabled={searching}>
          🧭 {searching ? t('common.loading') : t('home.useLocation')}
        </button>

        {statusMessage && <div style={styles.statusBanner(statusMessage.tone)}>{statusMessage.text}</div>}

        <div style={styles.mapCard}>
          {mapLoading && (
            <div style={styles.mapSkeleton}><div style={styles.skeletonPulse} /></div>
          )}

          {showSearchAreaBtn && (
            <button style={styles.searchAreaBtn} onClick={handleSearchThisArea}>
              🔍 Rechercher dans cette zone
            </button>
          )}

          <MapContainer
            center={mapCenter}
            zoom={APP_CONFIG.DEFAULT_MAP_ZOOM}
            style={{ width: '100%', height: '100%' }}
            ref={mapRef}
          >
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            <UserMoveDetector onUserMoved={handleUserMoved} />
            {!shouldFitBounds && <RecenterMap center={mapCenter} zoom={14} />}
            {shouldFitBounds && <FitToListings listings={visibleListings} userPosition={userPosition} />}

            {userPosition && (
              <Marker position={[userPosition.lat, userPosition.lng]} icon={userIcon}>
                <Popup>Vous êtes ici</Popup>
              </Marker>
            )}

            <ClusteredMarkers
              listings={visibleListings.filter((l) => l.lat && l.lng)}
              theme={theme}
              selectedId={selectedListing?.id}
              onSelectListing={handleSelectListing}
              onSelectCluster={handleSelectCluster}
            />
          </MapContainer>

          {selectedListing && (
            <div style={styles.selectedCardWrap}>
              <div style={styles.selectedCard} onClick={() => navigate(`/listing/${selectedListing.slug}`)}>
                <button
                  style={styles.closeCardBtn}
                  onClick={(e) => { e.stopPropagation(); setSelectedListing(null); }}
                >
                  ✕
                </button>
                <img src={selectedListing.images?.[0] || '/images/placeholder.png'} alt={selectedListing.title} style={styles.cardImage} />
                <div style={styles.cardContent}>
                  <div style={styles.cardTitle}>{selectedListing.title}</div>
                  <div style={styles.cardLocation}>
                    📍 {selectedListing.quartier}, {selectedListing.city}
                    {selectedListing.distanceKm != null && ` · ${selectedListing.distanceKm} km`}
                  </div>
                  <div style={styles.cardPrice}>
                    {formatPriceForMap(selectedListing.price, selectedListing.price_type || selectedListing.priceType)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={styles.navSpacer} />
      </div>

      <LocationPermissionModal open={showPermissionGuide} onClose={() => setShowPermissionGuide(false)} />
    </div>
  );
};

export default MapSearch;